import { CssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { getMuiTheme } from "./theme";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { ThemeMode } from "./theme.types";
import { useTranslation } from "react-i18next";
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
}
const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "dark", // default to dark so existing users have the same experience as before
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleMode: () => {},
});

export const useThemeMode = (): ThemeModeContextValue =>
  useContext(ThemeModeContext);

export const AppThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const subscriber = useMemo(
    () => (listener: () => void) =>
      window.carbonThemeStore?.subscribe(listener),
    []
  );
  const mode = useSyncExternalStore<ThemeMode>(subscriber, () =>
    window.carbonThemeStore?.get()
  );
  const { t, i18n } = useTranslation();
  const language = portalLanguageOrDefault(i18n.language);

  const [modeState, setModeState] = useState<"dark" | "light">(mode);
  const muiTheme = getMuiTheme(modeState, language, t);

  return (
    <ThemeModeContext.Provider
      value={{
        mode,
        toggleMode: () => {
          window.carbonThemeStore?.setPreference(
            mode === "dark" ? "light" : "dark"
          );
          setModeState(modeState === "dark" ? "light" : "dark");
        },
      }}
    >
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};
